import React from 'react'
import PropTypes from 'prop-types'
import { 
  ArrowRight, 
  ArrowDown, 
  Circle, 
  Square, 
  Triangle, 
  Users, 
  Building, 
  Zap,
  Target,
  TrendingUp,
  BarChart3
} from 'lucide-react'

const DiagramVisualization = ({ element }) => {
  // Parse diagram data from element
  const parseDiagram = (element) => {
    try {
      let data = element.data
      
      // If data is string, try to parse it
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data)
        } catch {
          // If parsing fails, create data from description
          const description = element.description || element.title || 'Diagram'
          return { type: 'process', data: createSampleDiagramData(description) }
        }
      }

      // If no data, create sample data based on element info
      if (!data) {
        const description = element.description || element.title || 'Process Flow'
        return { type: 'process', data: createSampleDiagramData(description) }
      }

      // Handle different diagram structures
      if (data.nodes && data.connections) {
        return { type: 'flowchart', data }
      }
      
      if (data.steps || data.processes) {
        return { type: 'process', data: data.steps || data.processes }
      }
      
      if (data.categories || data.sections) {
        return { type: 'infographic', data }
      }

      if (Array.isArray(data)) {
        return { type: 'process', data }
      }

      // If data is object with steps/items/nodes
      if (data && typeof data === 'object') {
        const items = data.items || data.processes || []
        if (Array.isArray(items)) {
          return { type: 'process', data: items }
        }
      }

      return { type: 'simple', data }
    } catch (error) {
      console.error('Error parsing diagram data:', error)
      return { type: 'process', data: createSampleDiagramData('Error - Sample Diagram') }
    }
  }

  // Create sample diagram data based on description
  const createSampleDiagramData = (description) => {
    const lowerDesc = description.toLowerCase()
    
    if (lowerDesc.includes('ai') || lowerDesc.includes('artificial') || lowerDesc.includes('machine learning')) {
      return [
        { title: 'Data Collection', description: 'Gather and prepare training data', icon: 'chart' },
        { title: 'Data Processing', description: 'Clean and transform data', icon: 'zap' },
        { title: 'Model Training', description: 'Train AI model with processed data', icon: 'target' },
        { title: 'Model Evaluation', description: 'Test and validate model performance', icon: 'trending' },
        { title: 'Deployment', description: 'Deploy model to production', icon: 'building' }
      ]
    }
    
    if (lowerDesc.includes('development') || lowerDesc.includes('software') || lowerDesc.includes('coding')) {
      return [
        { title: 'Planning', description: 'Define requirements and scope', icon: 'target' },
        { title: 'Design', description: 'Create system architecture', icon: 'square' },
        { title: 'Implementation', description: 'Write and develop code', icon: 'zap' },
        { title: 'Testing', description: 'Test functionality and performance', icon: 'chart' },
        { title: 'Deployment', description: 'Release to production', icon: 'building' }
      ]
    }
    
    if (lowerDesc.includes('business') || lowerDesc.includes('management') || lowerDesc.includes('strategy')) {
      return [
        { title: 'Analysis', description: 'Analyze current situation', icon: 'chart' },
        { title: 'Planning', description: 'Develop strategic plan', icon: 'target' },
        { title: 'Execution', description: 'Implement the plan', icon: 'zap' },
        { title: 'Monitoring', description: 'Track progress and metrics', icon: 'trending' },
        { title: 'Optimization', description: 'Improve and optimize', icon: 'building' }
      ]
    }
    
    // Default process flow
    return [
      { title: 'Start', description: 'Begin the process', icon: 'circle' },
      { title: 'Process 1', description: 'First step in the workflow', icon: 'square' },
      { title: 'Process 2', description: 'Second step in the workflow', icon: 'triangle' },
      { title: 'Decision', description: 'Make important decision', icon: 'target' },
      { title: 'End', description: 'Complete the process', icon: 'circle' }
    ]
  }

  const { type, data } = parseDiagram(element)

  const getIcon = (iconName) => {
    const icons = {
      users: Users,
      building: Building,
      zap: Zap,
      target: Target,
      trending: TrendingUp,
      chart: BarChart3,
      circle: Circle,
      square: Square,
      triangle: Triangle
    }
    
    const IconComponent = icons[iconName?.toLowerCase()] || Circle
    return <IconComponent className="w-6 h-6" />
  }

  const renderFlowchart = (data) => {
    if (!data.nodes || !Array.isArray(data.nodes)) return null

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.nodes.map((node, index) => (
            <div key={index} className="relative">
              <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center mb-2 text-purple-400">
                  {getIcon(node.icon)}
                </div>
                <h4 className="font-medium text-text-primary mb-1">{node.title || node.name}</h4>
                <p className="text-sm text-text-secondary">{node.description}</p>
              </div>
              {index < data.nodes.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2">
                  <ArrowRight className="w-4 h-4 text-text-secondary" />
                </div>
              )}
            </div>
          ))}
        </div>
        
        {data.connections && data.connections.length > 0 && (
          <div className="mt-4 text-xs text-text-secondary">
            <strong>Koneksi:</strong> {data.connections.join(' → ')}
          </div>
        )}
      </div>
    )
  }

  const renderProcess = (steps) => {
    if (!Array.isArray(steps)) return null

    return (
      <div className="space-y-3">
        {steps.map((step, index) => (
          <div key={index} className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
              {index + 1}
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-text-primary mb-1">
                {typeof step === 'object' ? step.title || step.name : step}
              </h4>
              {typeof step === 'object' && step.description && (
                <p className="text-sm text-text-secondary">{step.description}</p>
              )}
            </div>
            {index < steps.length - 1 && (
              <div className="flex-shrink-0 ml-4">
                <ArrowDown className="w-4 h-4 text-text-secondary" />
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  const renderInfographic = (data) => {
    const sections = data.categories || data.sections || []
    if (!Array.isArray(sections)) return null

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sections.map((section, index) => (
          <div key={index} className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-purple-400">
                {getIcon(section.icon)}
              </div>
              <h4 className="font-medium text-text-primary">
                {section.title || section.name}
              </h4>
            </div>
            
            {section.description && (
              <p className="text-sm text-text-secondary mb-2">{section.description}</p>
            )}
            
            {section.value && (
              <div className="text-2xl font-bold text-purple-400 mb-1">
                {section.value}
              </div>
            )}
            
            {section.items && Array.isArray(section.items) && (
              <ul className="text-sm text-text-secondary space-y-1">
                {section.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center gap-2">
                    <Circle className="w-2 h-2 text-purple-400 flex-shrink-0" />
                    {typeof item === 'object' ? item.name || item.title : item}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    )
  }

  const renderSimple = (data) => {
    return (
      <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
        <div className="text-center">
          <div className="text-purple-400 mb-2">
            {getIcon('chart')}
          </div>
          <h4 className="font-medium text-text-primary mb-2">{element.title}</h4>
          <p className="text-sm text-text-secondary">{element.description}</p>
          
          {data && (
            <div className="mt-3 text-xs text-text-secondary bg-bg-primary rounded p-2">
              <pre className="whitespace-pre-wrap">
                {typeof data === 'object' ? JSON.stringify(data, null, 2) : data}
              </pre>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderError = () => {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
        <div className="text-red-400 mb-2">
          <Circle className="w-6 h-6 mx-auto" />
        </div>
        <p className="text-sm text-red-400">Error loading diagram data</p>
      </div>
    )
  }

  const renderDiagram = () => {
    switch (type) {
      case 'flowchart':
        return renderFlowchart(data)
      case 'process':
        return renderProcess(data)
      case 'infographic':
        return renderInfographic(data)
      case 'error':
        return renderError()
      default:
        return renderSimple(data)
    }
  }

  return (
    <div className="w-full bg-bg-primary rounded-lg p-4 border border-border-primary">
      <div className="mb-3">
        <h4 className="font-medium text-text-primary flex items-center gap-2">
          <div className="text-green-400">
            {getIcon('chart')}
          </div>
          {element.title}
        </h4>
        {element.description && (
          <p className="text-sm text-text-secondary mt-1">{element.description}</p>
        )}
      </div>
      
      <div className="min-h-[200px]">
        {renderDiagram()}
      </div>
    </div>
  )
}

DiagramVisualization.propTypes = {
  element: PropTypes.shape({
    type: PropTypes.string.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    data: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string])
  }).isRequired
}

export default DiagramVisualization